<script setup>
import { computed, ref, watch, nextTick, onMounted } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import {
  StealieButton,
  StealieHeading,
  StealieParagraph,
  StealieText,
  StealieFileUpload,
} from "@get-overalls/fe-stealie";
import {
  ChevronLeftIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/vue/24/outline";
import { createMessage } from "@/services/MessageService";
import { getRelativeTime } from "@/utilities/getRelativeTime";
import { useRequestsStore } from "@/stores/requests";
import AuthenticatedLayout from "@/layouts/AuthenticatedLayout.vue";
import RequestMessage from "@/components/RequestMessage.vue";
import { getPresignedUrl } from "@/services/FileUploadService";

const route = useRoute();
const requestId = computed(() => route.params.requestId);
const requestsStore = useRequestsStore();
const { requestDetails } = storeToRefs(requestsStore);
const request = ref(requestDetails.value[requestId.value]);
const messagesContainerRef = ref(null);
const messageInputRef = ref(null);
const messageBody = ref("");
const requestExternalId = computed(() => request.value?.external_id);
const fileUploadRef = ref(null);
const uploadUrl = ref(null);
const uploadKey = ref(null);

const status = computed(() => {
  switch (request.value?.status) {
    case "open":
      return "In Queue";
    case "closed":
      return "Completed";
    case "in_progress":
      return "In Progress";
    default:
      return "Unknown";
  }
});

const handleFileUploadSuccess = async ({ reset: resetFileUploadStatus }) => {
  await createMessage(requestId.value, messageBody.value, uploadKey.value);
  messageBody.value = "";
  requestsStore.fetchRequest(requestId.value);
  resetFileUploadStatus();
};

const uploadError = ref("");
const handleUploadError = (error, fileName) => {
  uploadError.value = `There was an error uploading your file: ${fileName}. We have been notified of the error, but you are welcome to try again.`;
  console.error("Message File Upload Error", error.message);
};

const addMessage = async () => {
  uploadKey.value = null;
  uploadUrl.value = null;
  messageInputRef.value.focus();
  if (messageBody.value.trim() === "") return;
  if (fileUploadRef.value.hasFiles) {
    const { presignedUrl, key } = await getPresignedUrl({
      filename: fileUploadRef.value.files[0].name,
      requestId: requestExternalId.value,
    });
    uploadUrl.value = presignedUrl;
    uploadKey.value = key;
    await nextTick();
    fileUploadRef.value.upload();
    // The uploader's `upload-success` event will call `createMessage` once the upload is complete
    return;
  }
  await createMessage(requestId.value, messageBody.value);
  messageBody.value = "";
  requestsStore.fetchRequest(requestId.value);
};

const scrollToBottom = () => {
  nextTick(() => {
    const scrollContainer = document.documentElement;
    scrollContainer.scrollTo({
      top: scrollContainer.scrollHeight,
      behavior: "instant",
    });
  });
};

watch(
  () => requestDetails.value[requestId.value],
  (newVal) => {
    request.value = newVal;
    scrollToBottom();
  },
);

onMounted(() => {
  nextTick(() => {
    scrollToBottom();
  });
});
</script>

<template>
  <AuthenticatedLayout no-padding>
    <template #mobile-header>
      <div v-if="request" class="flex items-start gap-x-3 py-2">
        <RouterLink
          :to="{ name: 'Requests' }"
          class="inline-block rounded-lg border border-brand-gray-50 p-3 text-brand-gray-400 hover:text-brand-gray-600"
        >
          <ChevronLeftIcon class="size-6" />
          <span class="sr-only">Back to requests</span>
        </RouterLink>
        <div>
          <StealieHeading level="1" class="!text-[26px]" data-sr-mask>{{
            request.name
          }}</StealieHeading>
          <StealieParagraph class="mb-2" data-sr-unmask>
            <StealieText class="text-black" bold> {{ status }}:</StealieText>
            <StealieText class="text-brand-gray-600">
              Requested {{ getRelativeTime(request.issued_at) }}
            </StealieText>
          </StealieParagraph>
        </div>
      </div>
    </template>
    <header
      v-if="request"
      class="sticky top-0 z-10 mx-8 hidden items-start rounded-b-lg border border-[#edeff0] bg-white px-8 py-6 shadow-[0px_-6px_10px_rgba(0,0,0,0.05)] lg:block"
    >
      <StealieHeading level="1" class="!text-[26px]" data-sr-mask>{{
        request.name
      }}</StealieHeading>
      <StealieParagraph class="mb-2" data-sr-unmask>
        <StealieText class="text-black" bold>{{ status }}:</StealieText>
        <StealieText class="text-brand-gray-600">
          Requested {{ getRelativeTime(request.issued_at) }}
        </StealieText>
      </StealieParagraph>
    </header>
    <section
      ref="messagesContainerRef"
      :class="[
        'flex flex-1 flex-col items-stretch gap-y-8 overflow-y-auto px-4 py-4 sm:gap-y-12 sm:px-8 lg:py-8',
      ]"
    >
      <template v-if="request?.messages.length === 0">
        <div
          class="flex max-w-72 flex-1 flex-col items-center justify-center self-center"
          data-sr-unmask
        >
          <ChatBubbleLeftRightIcon
            class="mb-2 size-36 stroke-[0.5] text-brand-gray-200"
          />
          <StealieText as="h2" size="lg" bold class="mb-4 font-bold text-brand-gray-500">
            No messages yet
          </StealieText>
          <StealieParagraph size="md" class="text-center text-brand-gray-500">
            Use the
            <StealieButton variant="tertiary" @click="messageInputRef.focus()">
              message field
            </StealieButton>
            below to communicate with your LifeConcierge
          </StealieParagraph>
        </div>
      </template>
      <template v-else>
        <RequestMessage
          v-for="message in request?.messages"
          :key="message.id"
          :message="message.body_html"
          :author="message.source_name"
          :source="message.source"
          :timestamp="message.external_created_at"
          :attachments="message.attachment_keys"
        />
      </template>
    </section>

    <footer
      class="sticky bottom-0 mx-4 mt-auto rounded-t-lg border border-[#edeff0] bg-white p-2 shadow-[0px_-6px_10px_0px_rgba(0,0,0,0.05)] sm:mx-8 sm:p-4"
    >
      <form class="flex flex-col" @submit.prevent="addMessage">
        <textarea
          ref="messageInputRef"
          v-model="messageBody"
          class="min-h-17 w-full rounded-md border border-brand-gray-100 px-3 pt-2 pb-10 text-base text-gray-900 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus-highlight-light sm:text-sm/6"
          placeholder="Enter your message"
          :required="true"
        />
        <div class="mt-2 flex items-center justify-between">
          <div class="sm:min-w-64">
            <StealieFileUpload
              ref="fileUploadRef"
              :url="uploadUrl"
              :file-limit="1"
              simple
              accept="image/*,application/pdf"
              invalid-file-type-message="'{0}' has an invalid file type. Only images or PDFs are allowed."
              data-sr-mask
              @upload-success="handleFileUploadSuccess"
              @upload-error="handleUploadError"
            />
          </div>
          <button
            class="shrink-0 cursor-pointer self-end rounded p-1.5 text-brand-primary-teal-400 focus-visible:outline-focus-highlight-light active:text-brand-primary-teal-600 aria-disabled:cursor-not-allowed aria-disabled:text-brand-gray-100"
            type="submit"
            aria-label="Send"
          >
            <PaperAirplaneIcon
              class="pointer-events-none size-5 sm:size-6"
              aria-hidden="true"
            />
          </button>
        </div>
      </form>
    </footer>
  </AuthenticatedLayout>
</template>
